import React, { useMemo } from 'react';
import { cn } from '../lib/utils';

const ScissorsMascot = ({ 
  size = 'md', 
  className = '', 
  lookAt = 0, // -1 to 1 (left to right)
  lookAtY = 0, // -1 to 1 (top to bottom)
  isPassword = false,
  isPeeking = false,
  validationMessage = ''
}) => {
  const sizes = {
    sm: 'w-12 h-12',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-48 h-48',
  };

  // Calculate pupil position based on lookAt
  const pupilOffsetX = useMemo(() => lookAt * 5, [lookAt]);
  const pupilOffsetY = useMemo(() => lookAtY * 3, [lookAtY]);

  return (
    <div className={cn("relative flex flex-col items-center", sizes[size], className)} data-testid="scissors-mascot">
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-xl"
      >
        <defs>
          <linearGradient id="bladeGradientLeft" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="50%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#64748b" />
          </linearGradient>
          <linearGradient id="bladeGradientRight" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#64748b" />
            <stop offset="50%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#94a3b8" />
          </linearGradient>
        </defs>

        {/* Shadow for depth */}
        <ellipse cx="50" cy="90" rx="30" ry="5" fill="black" fillOpacity="0.1" />

        {/* Scissors Pivot Point */}
        <circle cx="50" cy="50" r="4" fill="#3f3f46" zIndex="10" />

        {/* Left Blade and Handle */}
        <g className="transition-all duration-500" style={{ transformOrigin: '50px 50px', transform: isPassword ? 'rotate(-12deg)' : 'rotate(0deg)' }}>
          {/* Blade - Realistic curved shape */}
          <path 
            d="M50 50 L35 85 C32 92, 40 95, 48 90 C50 88, 50 70, 50 50" 
            fill="url(#bladeGradientLeft)" 
            stroke="#64748b" 
            strokeWidth="0.5" 
          />
          {/* Cutting Edge */}
          <path 
            d="M50 55 L40 88 C43 90, 48 88, 50 60 Z" 
            fill="white" 
            opacity="0.3" 
          />
          
          {/* Handle */}
          <path d="M50 50 L65 15 C75 10, 85 20, 80 30 L65 40 Z" fill="#ef4444" stroke="#b91c1c" strokeWidth="1" />
          <path d="M72 22 C76 18, 80 22, 78 26 C76 30, 72 26, 72 22" fill="#09090b" /> {/* Hole */}
        </g>

        {/* Right Blade and Handle */}
        <g className="transition-all duration-500" style={{ transformOrigin: '50px 50px', transform: isPassword ? 'rotate(12deg)' : 'rotate(0deg)' }}>
          {/* Blade - Realistic curved shape */}
          <path 
            d="M50 50 L65 85 C68 92, 60 95, 52 90 C50 88, 50 70, 50 50" 
            fill="url(#bladeGradientRight)" 
            stroke="#94a3b8" 
            strokeWidth="0.5" 
          />
          {/* Cutting Edge */}
          <path 
            d="M50 55 L60 88 C57 90, 52 88, 50 60 Z" 
            fill="white" 
            opacity="0.2" 
          />
          
          {/* Handle */}
          <path d="M50 50 L35 15 C25 10, 15 20, 20 30 L35 40 Z" fill="#ef4444" stroke="#b91c1c" strokeWidth="1" />
          <path d="M28 22 C24 18, 20 22, 22 26 C24 30, 28 26, 28 22" fill="#09090b" /> {/* Hole */}
        </g>

        {/* Eyes - Positioned on the handles for a character look */}
        <g transform="translate(0, -5)">
          {/* Eyes White */}
          <ellipse cx="40" cy="45" rx="9" ry="11" fill="white" stroke="#09090b" strokeWidth="0.5" className="transition-all duration-300" />
          <ellipse cx="60" cy="45" rx="9" ry="11" fill="white" stroke="#09090b" strokeWidth="0.5" className="transition-all duration-300" />
          
          {isPassword ? (
            <>
              {/* Closed Eyes */}
              <path d="M34 45 Q40 50 46 45" stroke="#09090b" strokeWidth="2" strokeLinecap="round" fill="none" />
              <path d="M54 45 Q60 50 66 45" stroke="#09090b" strokeWidth="2" strokeLinecap="round" fill="none" />
            </>
          ) : isPeeking ? (
            <>
              {/* Peeking Eyes */}
              <rect x="30" y="34" width="20" height="7" fill="#ef4444" />
              <rect x="50" y="34" width="20" height="7" fill="#ef4444" />
              
              {/* Pupils */}
              <circle cx={40 + pupilOffsetX} cy={47 + pupilOffsetY} r="3.5" fill="#09090b" className="transition-all duration-200" />
              <circle cx={60 + pupilOffsetX} cy={47 + pupilOffsetY} r="3.5" fill="#09090b" className="transition-all duration-200" />
            </>
          ) : (
            <>
              {/* Normal Pupils */}
              <circle cx={40 + pupilOffsetX} cy={45 + pupilOffsetY} r="4.5" fill="#09090b" className="transition-all duration-200" />
              <circle cx={60 + pupilOffsetX} cy={45 + pupilOffsetY} r="4.5" fill="#09090b" className="transition-all duration-200" />
              
              {/* Eye shine */}
              <circle cx={40 + pupilOffsetX + 2} cy={43 + pupilOffsetY - 2} r="1.5" fill="white" className="transition-all duration-200" />
              <circle cx={60 + pupilOffsetX + 2} cy={43 + pupilOffsetY - 2} r="1.5" fill="white" className="transition-all duration-200" />
            </>
          )}
        </g>
      </svg>

      {/* Validation Message Bubble */}
      {validationMessage && (
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-white text-black text-[10px] font-bold px-3 py-2 rounded-2xl whitespace-nowrap shadow-lg animate-bounce border-2 border-red-500 after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-white">
          {validationMessage}
        </div>
      )}
    </div>
  );
};

export default ScissorsMascot;
